import React, { useEffect, useRef, useState } from "react";
import { PDFPreview } from "./pdf-preview.tsx";
import type {
  Office,
  OfficeListItem,
  User,
  Config,
  ConfigVersion,
  Reference,
  FileRecord,
  Permission,
} from "../shared/contracts.ts";
import { statusLabels } from "../shared/contracts.ts";
import {
  api,
  apiPDF,
  date,
  money,
  bytes,
  fileUrl,
  Icon,
  Button,
  Banner,
  Badge,
  Empty,
  Field,
  Modal,
  tabKeys,
  permissionLabels,
} from "./ui.tsx";
import type { PublicIdentity, Notice, Integration } from "./ui.tsx";
export function OfficeList({
  user,
  config,
  online,
  integrations,
  query,
  setQuery,
  filter,
  setFilter,
  initialScroll,
  listPage,
  setListPage,
  onOpen,
  failed,
}: {
  user: User;
  config: Config | null;
  online: boolean;
  integrations: Integration[];
  query: string;
  setQuery: (s: string) => void;
  filter: string;
  setFilter: (s: string) => void;
  initialScroll: number;
  listPage: number;
  setListPage: React.Dispatch<React.SetStateAction<number>>;
  onOpen: (id: string) => void;
  failed: (e: any) => void;
}) {
  const [offices, setOffices] = useState<OfficeListItem[] | null>(null),
    [creating, setCreating] = useState(false),
    [busy, setBusy] = useState(false),
    [total, setTotal] = useState(0),
    [listError, setListError] = useState(""),
    [listEpoch, setListEpoch] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setOffices(null);
    setListError("");
    const timeout = setTimeout(() => {
      const params = new URLSearchParams({
        paged: "1",
        limit: "20",
        offset: String((listPage - 1) * 20),
        filter:
          filter === "in_progress"
            ? "processing"
            : filter === "awaiting_response"
              ? "response"
              : filter,
        search: query,
      });
      api<{
        items: OfficeListItem[];
        total: number;
      }>("/offices?" + params, "GET", undefined, controller.signal)
        .then((result) => {
          if (!controller.signal.aborted) {
            if (result.total > 0 && !result.items.length && listPage > 1) {
              setListPage(Math.ceil(result.total / 20));
              return;
            }
            setOffices(result.items);
            setTotal(result.total);
          }
        })
        .catch((e) => {
          if (e.name !== "AbortError") {
            setListError(e.message);
            failed(e);
          }
        });
    }, 200);
    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [query, filter, listPage, listEpoch]);
  useEffect(() => {
    if (offices !== null && initialScroll)
      requestAnimationFrame(() => window.scrollTo({ top: initialScroll }));
  }, [offices !== null]);
  const can = (p: Permission) => user.admin || user.permissions.includes(p);
  const filtered = offices;
  const tabs = [
    ["draft", "Rascunhos"],
    ["in_progress", "Em processamento"],
    ["awaiting_response", "Aguardando resposta"],
    ["closed", "Finalizados com resposta"],
    ["all", "Todos"],
  ];
  return (
    <>
      <div className="page-head">
        <div>
          <span className="eyebrow">REDAÇÃO E ACOMPANHAMENTO</span>
          <h1>Ofícios</h1>
          <p>Do pedido ao protocolo, com cada etapa à vista.</p>
        </div>
        {can("create") && (
          <Button
            kind="primary"
            icon="plus"
            disabled={!online}
            onClick={() => setCreating(true)}
          >
            Criar ofício
          </Button>
        )}
      </div>
      {integrations.some((i) => !i.ready) && (
        <details className="integration-summary">
          <summary>
            <Icon name="alert" size={18} />
            {integrations.filter((i) => !i.ready).length}{" "}
            {integrations.filter((i) => !i.ready).length === 1
              ? "integração requer configuração"
              : "integrações requerem configuração"}
            <span>Ver detalhes</span>
          </summary>
          <div>
            {integrations.map((i) => (
              <div className="integration-row" key={i.id}>
                <span
                  className={"status-dot " + (i.ready ? "ready" : "pending")}
                />
                <div>
                  <strong>{i.name}</strong>
                  <p>{i.message}</p>
                </div>
                <span className={"badge " + (i.ready ? "success" : "warn")}>
                  {i.ready ? "Disponível" : "Pendente"}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
      <section className="list-panel">
        <div
          className="tabs"
          role="tablist"
          onKeyDown={tabKeys}
          aria-label="Situação dos ofícios"
        >
          {tabs.map(([id, label]) => (
            <button
              role="tab"
              key={id}
              aria-selected={filter === id}
              onClick={() => setFilter(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="list-toolbar">
          <div className="search">
            <Icon name="search" size={18} />
            <input
              aria-label="Pesquisar ofícios"
              placeholder="Buscar por assunto, número ou protocolo"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button aria-label="Limpar pesquisa" onClick={() => setQuery("")}>
                <Icon name="close" size={17} />
              </button>
            )}
          </div>
          <span>
            {offices === null ? "…" : total}{" "}
            {total === 1 ? "ofício" : "ofícios"}
          </span>
        </div>
        {listError ? (
          <div className="list-error">
            <Banner kind="danger">{listError}</Banner>
            <Button icon="refresh" onClick={() => setListEpoch((n) => n + 1)}>
              Tentar carregar novamente
            </Button>
          </div>
        ) : offices === null ? (
          <div className="loading">
            <span className="spinner" />
            Carregando ofícios…
          </div>
        ) : filtered?.length ? (
          <div className="table-scroll">
            <table className="office-table">
              <thead>
                <tr>
                  <th>Documento e assunto</th>
                  <th>Destinatário</th>
                  <th>Situação</th>
                  <th>Atualização</th>
                  <th>
                    <span className="sr-only">Abrir</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <button
                        className="table-link"
                        onClick={() => onOpen(o.id)}
                      >
                        {o.number
                          ? `Ofício ${o.number}/${o.year}`
                          : o.title || "Rascunho sem título"}
                      </button>
                      <p>{o.subject || "Assunto ainda não informado"}</p>
                      {o.solarNumber && <small>Solar {o.solarNumber}</small>}
                    </td>
                    <td>
                      {o.recipientName ||
                        config?.recipients.find((r) => r.id === o.recipientId)
                          ?.name ||
                        "A definir"}
                    </td>
                    <td>
                      <Badge status={o.status} />
                      {o.pendingEffects.length > 0 && (
                        <small className="pending-note">
                          Integração pendente
                        </small>
                      )}
                    </td>
                    <td className="nowrap">{date(o.updatedAt)}</td>
                    <td>
                      <button
                        className="icon-button"
                        aria-label={"Abrir " + (o.title || "ofício")}
                        onClick={() => onOpen(o.id)}
                      >
                        <Icon name="arrow" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pagination">
              <span>
                Itens {(listPage - 1) * 20 + 1} a{" "}
                {Math.min(listPage * 20, total)} de {total}
              </span>
              <Button
                disabled={listPage === 1}
                onClick={() => setListPage((n) => Math.max(1, n - 1))}
              >
                Anterior
              </Button>
              <span>
                Página {listPage} de {Math.ceil(total / 20)}
              </span>
              <Button
                disabled={listPage * 20 >= total}
                onClick={() => setListPage((n) => n + 1)}
              >
                Próxima
              </Button>
            </div>
          </div>
        ) : (
          <Empty
            title={
              query
                ? "Nenhum resultado encontrado"
                : filter === "draft"
                  ? "Um novo ofício começa por aqui"
                  : "Nenhum ofício nesta situação"
            }
          >
            {query
              ? "Ajuste a busca ou limpe os filtros para consultar outros documentos."
              : filter === "draft"
                ? "Descreva a solicitação, reúna os documentos e use a IA para preparar o texto."
                : "Os documentos aparecerão aqui conforme o andamento real do processo."}
          </Empty>
        )}
      </section>
      {creating && (
        <Modal title="Criar ofício" onClose={() => setCreating(false)}>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              const f = new FormData(e.currentTarget);
              try {
                const o = await api<Office>("/offices", "POST", {
                  title: String(f.get("title")),
                  request: String(f.get("request")),
                  subject: String(f.get("subject")),
                  recipientId: String(f.get("recipientId")),
                  requesterName: "",
                  requesterPhone: "",
                  body: "",
                });
                onOpen(o.id);
              } catch (e) {
                failed(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            <p className="modal-intro">
              O rascunho não recebe numeração oficial.
            </p>
            <Field label="Título para organização">
              <input name="title" autoFocus maxLength={240} />
            </Field>
            <Field label="O que você deseja solicitar?">
              <textarea
                name="request"
                rows={5}
                required
                placeholder="Descreva o pedido e as informações confirmadas do caso."
              />
            </Field>
            <Field label="Resumo do assunto">
              <input name="subject" maxLength={300} />
            </Field>
            <Field label="Destinatário">
              <select name="recipientId" defaultValue="">
                <option value="">Definir durante a redação</option>
                {config?.recipients
                  .filter((r) => r.active)
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
              </select>
            </Field>
            <div className="modal-actions">
              <Button type="button" onClick={() => setCreating(false)}>
                Cancelar
              </Button>
              <Button type="submit" kind="primary" busy={busy}>
                Criar rascunho
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
type Draft = Pick<
  Office,
  | "title"
  | "request"
  | "subject"
  | "recipientId"
  | "requesterName"
  | "requesterPhone"
  | "body"
>;
const draftOf = (o: Office): Draft => ({
  title: o.title,
  request: o.request,
  subject: o.subject,
  recipientId: o.recipientId,
  requesterName: o.requesterName,
  requesterPhone: o.requesterPhone,
  body: o.body,
});
export function OfficeEditor({
  id,
  user,
  config,
  online,
  onBack,
  onOpen,
  notify,
  failed,
  onDirty,
}: {
  id: string;
  user: User;
  config: Config | null;
  online: boolean;
  onBack: () => void;
  onOpen: (id: string) => void;
  notify: (s: string) => void;
  failed: (e: any) => void;
  onDirty: (d: boolean) => void;
}) {
  const [o, setO] = useState<Office | null>(null),
    [draft, setDraft] = useState<Draft | null>(null),
    [dirty, setDirty] = useState(false),
    [baseVersion, setBaseVersion] = useState(0),
    [busy, setBusy] = useState(""),
    [panel, setPanel] = useState("ai"),
    [instruction, setInstruction] = useState(""),
    [scope, setScope] = useState("all"),
    [paragraph, setParagraph] = useState(1),
    [files, setFiles] = useState<FileRecord[]>([]),
    [preview, setPreview] = useState<string | null>(null),
    [draftPreview, setDraftPreview] = useState<string | null>(null),
    [modal, setModal] = useState(""),
    [people, setPeople] = useState<User[]>([]),
    [sharing, setSharing] = useState<string[]>([]),
    [driveId, setDriveId] = useState(""),
    [conflict, setConflict] = useState(false);
  const dirtyRef = useRef(false);
  const activeEditor = useRef<string | null>(id);
  dirtyRef.current = dirty;
  const can = (p: Permission) => user.admin || user.permissions.includes(p);
  const editable = !!o && o.status === "draft" && can("edit");
  const replace = (next: Office) => {
    setO(next);
    setDraft(draftOf(next));
    setBaseVersion(next.version);
    setDirty(false);
    onDirty(false);
    setConflict(false);
  };
  const reload = async () => {
    const next = await api<Office>("/offices/" + id);
    setO(next);
    if (!dirtyRef.current) {
      setDraft(draftOf(next));
      setBaseVersion(next.version);
    } else if (next.version !== baseVersion) setConflict(true);
    return next;
  };
  useEffect(() => {
    activeEditor.current = id;
    setDraftPreview(null);
    api<Office>("/offices/" + id)
      .then(replace)
      .catch(failed);
    return () => {
      activeEditor.current = null;
      onDirty(false);
    };
  }, [id]);
  useEffect(() => {
    return () => {
      if (draftPreview) URL.revokeObjectURL(draftPreview);
    };
  }, [draftPreview]);
  useEffect(() => {
    if (!o) return;
    let active = true;
    Promise.all(
      o.attachmentIds.map((id) => api<FileRecord>("/file-metadata/" + id)),
    )
      .then((next) => {
        if (active) setFiles(next);
      })
      .catch((error) => {
        if (active) failed(error);
      });
    return () => {
      active = false;
    };
  }, [o?.attachmentIds.join(",")]);
  useEffect(() => {
    if (
      !o ||
      ![
        "finalizing",
        "queued",
        "preparing",
        "uploading",
        "awaiting_signature",
        "verifying",
        "protocolled",
      ].includes(o.status)
    )
      return;
    const timer = setInterval(() => {
      if (navigator.onLine) reload().catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [o?.status, baseVersion]);
  const change = (key: keyof Draft, value: string) => {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
    setDirty(true);
    onDirty(true);
  };
  const run = async (name: string, fn: () => Promise<void>) => {
    setBusy(name);
    try {
      await fn();
    } catch (e) {
      failed(e);
    } finally {
      setBusy("");
    }
  };
  const save = async () => {
    if (!o || !draft) return;
    const updated = await api<Office>("/offices/" + id, "PATCH", {
      version: baseVersion,
      ...draft,
    });
    replace(updated?.id ? updated : await api("/offices/" + id));
    notify("Rascunho confirmado no servidor.");
  };
  if (!o || !draft)
    return (
      <div className="loading">
        <span className="spinner" />
        Abrindo ofício…
      </div>
    );
  const currentSuggestion = o.suggestions.filter((s) => s.state === "pending");
  const noUnsaved = online && !dirty && !conflict && !busy;
  const imageIds =
    o.status === "draft"
      ? config?.layout.appendImages !== false
        ? files
            .filter((file) => file.mime.startsWith("image/"))
            .map((file) => file.id)
        : []
      : o.pdfImageIds || [];
  const embeddedIds = new Set(imageIds);
  const imageFiles = imageIds
    .map((imageId) => files.find((file) => file.id === imageId))
    .filter((file): file is FileRecord => !!file);
  const complementaryFiles = files.filter((file) => !embeddedIds.has(file.id));
  const attachmentsLoaded =
    files.length === o.attachmentIds.length &&
    files.every((file, index) => file.id === o.attachmentIds[index]);
  const showDraftPreview = () =>
    run("preview", async () => {
      try {
        const pdf = await apiPDF("/offices/" + id + "/preview", {
          version: baseVersion,
        });
        if (activeEditor.current === id)
          setDraftPreview(URL.createObjectURL(pdf));
      } catch (error) {
        if ((error as { code?: string }).code === "VERSION_CONFLICT")
          setConflict(true);
        throw error;
      }
    });
  const moveImage = (fileId: string, direction: -1 | 1) =>
    run("order", async () => {
      const position = imageIds.indexOf(fileId);
      const adjacent = imageIds[position + direction];
      if (!adjacent) return;
      const ids = [...o.attachmentIds];
      const first = ids.indexOf(fileId);
      const second = ids.indexOf(adjacent);
      [ids[first], ids[second]] = [ids[second], ids[first]];
      try {
        const next = await api<Office>(
          "/offices/" + id + "/attachments/order",
          "PATCH",
          {
            ids,
            version: baseVersion,
          },
        );
        replace(next);
        setFiles(
          ids.map((fileId) => files.find((file) => file.id === fileId)!),
        );
        notify("Ordem das imagens confirmada no servidor.");
      } catch (error) {
        if ((error as { code?: string }).code === "VERSION_CONFLICT")
          await reload();
        throw error;
      }
    });
  return (
    <>
      <div className="editor-head">
        <div className="editor-context">
          <Button kind="ghost" icon="back" onClick={onBack}>
            Ofícios
          </Button>
          <span className="divider" />
          <span className="eyebrow">
            {o.number
              ? `OFÍCIO ${o.number}/${o.year}`
              : "RASCUNHO · SEM NÚMERO OFICIAL"}
          </span>
          <Badge status={o.status} />
        </div>
        <div className="editor-actions">
          {o.status === "draft" && (
            <Button
              icon="eye"
              busy={busy === "preview"}
              disabled={!noUnsaved || !attachmentsLoaded}
              title={
                dirty
                  ? "Salve o rascunho para conferir as alterações no PDF."
                  : undefined
              }
              onClick={showDraftPreview}
            >
              Prévia do PDF
            </Button>
          )}
          {can("share") && (
            <Button
              icon="users"
              disabled={!online || !!busy || dirty}
              onClick={() =>
                run("share-load", async () => {
                  setPeople(await api("/users"));
                  setSharing(o.sharedWith);
                  setModal("share");
                })
              }
            >
              Compartilhar
            </Button>
          )}
          {editable && (
            <Button
              icon="save"
              busy={busy === "save"}
              disabled={!online || !dirty || !!busy}
              onClick={() => run("save", save)}
            >
              Salvar rascunho
            </Button>
          )}
          {o.status === "draft" && can("finalize") && (
            <Button
              kind="primary"
              icon="check"
              disabled={
                !noUnsaved ||
                !attachmentsLoaded ||
                !draft.body.trim() ||
                !draft.subject.trim() ||
                !config?.recipients.some(
                  (r) => r.id === draft.recipientId && r.active,
                )
              }
              onClick={() => setModal("finalize")}
            >
              Finalizar ofício
            </Button>
          )}
          {o.status === "finalized" && can("protocol") && (
            <Button
              kind="primary"
              icon="send"
              disabled={!noUnsaved || !attachmentsLoaded}
              onClick={() => setModal("protocol")}
            >
              Protocolar
            </Button>
          )}
        </div>
      </div>
      <div className="document-title">
        <h1>
          {o.number
            ? `Ofício ${o.number}/${o.year}`
            : draft.title || "Novo ofício"}
        </h1>
        <p>
          <span className={dirty ? "unsaved" : ""}>
            {dirty
              ? "● Alterações ainda não salvas"
              : `Confirmado no servidor · ${date(o.updatedAt)}`}
          </span>
          {o.previousOfficeId && (
            <>
              {" "}
              ·{" "}
              <button
                className="text-button"
                onClick={() => onOpen(o.previousOfficeId!)}
              >
                Consultar versão anterior
              </button>
            </>
          )}
        </p>
      </div>
      {busy && (
        <Banner>
          {busy === "save"
            ? "Salvando o rascunho no servidor…"
            : busy === "suggest"
              ? "Gerando a proposta de redação…"
              : busy === "preview"
                ? "Preparando a prévia do PDF com os arquivos confirmados…"
                : busy === "order"
                  ? "Salvando a ordem das imagens no servidor…"
                  : "Processando sua solicitação…"}{" "}
          Os campos ficam temporariamente bloqueados para preservar suas
          edições. Você pode continuar consultando o documento e os anexos.
        </Banner>
      )}
      {o.status === "draft" &&
        (!draft.body.trim() ||
          !draft.subject.trim() ||
          !config?.recipients.some(
            (r) => r.id === draft.recipientId && r.active,
          )) && (
          <Banner>
            Para finalizar, confira:{" "}
            {[
              [!draft.subject.trim(), "resumo do assunto"],
              [
                !config?.recipients.some(
                  (r) => r.id === draft.recipientId && r.active,
                ),
                "destinatário ativo",
              ],
              [!draft.body.trim(), "texto do ofício"],
            ]
              .filter(([missing]) => missing)
              .map(([, label]) => label)
              .join(", ")}
            .
          </Banner>
        )}
      {conflict && (
        <Banner kind="warn">
          Outra alteração foi confirmada no servidor. Seu texto foi preservado
          nesta tela. Copie o que deseja manter e use{" "}
          <button
            className="text-button"
            onClick={() => {
              if (
                window.confirm(
                  "Substituir o texto desta tela pela versão atual do servidor?",
                )
              )
                run("reload", async () => replace(await api("/offices/" + id)));
            }}
          >
            carregar a versão atual
          </button>{" "}
          antes de salvar.
        </Banner>
      )}
      {(o.error || o.attempt?.error) && (
        <Banner kind="warn">{o.error || o.attempt?.error}</Banner>
      )}
      {o.pendingEffects.length > 0 && (
        <Banner kind="warn">
          O documento está preservado. Há etapas de integração pendentes:{" "}
          {o.pendingEffects.join(", ")}. Retentar recupera somente as etapas
          aplicáveis.
        </Banner>
      )}
      {[
        "queued",
        "preparing",
        "uploading",
        "awaiting_signature",
        "verifying",
        "recoverable_failure",
        "awaiting_review",
        "finalizing",
        "protocolled",
      ].includes(o.status) && (
        <section className="process-strip">
          <div className="process-symbol">
            <Icon
              name={o.status === "protocolled" ? "check" : "clock"}
              size={23}
            />
          </div>
          <div>
            <strong>{statusLabels[o.status]}</strong>
            <p>
              {o.status === "protocolled"
                ? `Protocolo Solar ${o.solarNumber || "confirmado"} · ${date(o.protocolledAt)}`
                : o.attempt?.checkpoint ||
                  "O andamento é atualizado conforme a confirmação das etapas."}
            </p>
            {o.attempt && <small>Tentativa {o.attempt.id}</small>}
          </div>
          <div className="process-actions">
            {o.status === "awaiting_signature" && o.attempt?.signatureUrl && (
              <a
                className="button primary"
                href={o.attempt.signatureUrl}
                target="_blank"
                rel="noreferrer"
              >
                Assinar no Solar <Icon name="link" size={17} />
              </a>
            )}
            {[
              "recoverable_failure",
              "awaiting_review",
              "awaiting_signature",
            ].includes(o.status) &&
              can("protocol") && (
                <Button
                  icon="refresh"
                  busy={busy === "retry"}
                  disabled={!online || !!busy}
                  onClick={() =>
                    run("retry", async () => {
                      await api("/offices/" + id + "/retry", "POST");
                      await reload();
                      notify("Pedido de recuperação recebido pelo servidor.");
                    })
                  }
                >
                  {o.status === "awaiting_review"
                    ? "Verificar resultado"
                    : "Verificar e retomar"}
                </Button>
              )}
            {o.receiptFileId && (
              <Button icon="eye" onClick={() => setPreview(o.receiptFileId!)}>
                Comprovante
              </Button>
            )}
          </div>
        </section>
      )}
      <div className="editor-grid">
        <section className="writing-panel" aria-busy={!!busy}>
          <div className="section-head">
            <div>
              <h2>Documento</h2>
              <p>Dados confirmados do caso e redação final.</p>
            </div>
            <span className="badge neutral">
              <Icon name={editable ? "document" : "check"} size={14} />
              {editable ? "Edição livre" : "Versão preservada"}
            </span>
          </div>
          <div className="document-fields">
            <Field label="Título para organização">
              <input
                value={draft.title}
                onChange={(e) => change("title", e.target.value)}
                disabled={!editable || !!busy}
                maxLength={200}
              />
            </Field>
            <div className="field-grid">
              <Field label="Destinatário">
                <select
                  value={draft.recipientId}
                  onChange={(e) => change("recipientId", e.target.value)}
                  disabled={!editable || !!busy}
                >
                  <option value="">Selecione um destinatário</option>
                  {draft.recipientId &&
                    !o.recipientName &&
                    !config?.recipients.some(
                      (r) => r.id === draft.recipientId,
                    ) && (
                      <option value={draft.recipientId}>
                        Destinatário removido ({draft.recipientId})
                      </option>
                    )}
                  {o.recipientName && !editable ? (
                    <option value={o.recipientId}>{o.recipientName}</option>
                  ) : (
                    config?.recipients
                      .filter((r) => r.active || r.id === draft.recipientId)
                      .map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                          {r.active ? "" : " (inativo)"}
                        </option>
                      ))
                  )}
                </select>
              </Field>
              <Field label="Resumo do assunto">
                <input
                  value={draft.subject}
                  onChange={(e) => change("subject", e.target.value)}
                  disabled={!editable || !!busy}
                  maxLength={300}
                />
              </Field>
            </div>
            <details className="request-details" open={!draft.body}>
              <summary>Solicitação e dados do requerente</summary>
              <Field label="Solicitação original">
                <textarea
                  rows={4}
                  value={draft.request}
                  onChange={(e) => change("request", e.target.value)}
                  disabled={!editable || !!busy}
                />
              </Field>
              <div className="field-grid">
                <Field label="Requerente">
                  <input
                    value={draft.requesterName}
                    onChange={(e) => change("requesterName", e.target.value)}
                    disabled={!editable || !!busy}
                  />
                </Field>
                <Field label="Celular do requerente">
                  <input
                    type="tel"
                    value={draft.requesterPhone}
                    onChange={(e) => change("requesterPhone", e.target.value)}
                    disabled={!editable || !!busy}
                  />
                </Field>
              </div>
            </details>
          </div>
          <div className="paper">
            <div className="paper-top">
              <span>{config?.layout.heading || "Redação do ofício"}</span>
              {o.number && (
                <strong>
                  Nº {o.number}/{o.year}
                </strong>
              )}
            </div>
            <label className="sr-only" htmlFor="office-body">
              Texto do ofício
            </label>
            <textarea
              id="office-body"
              className="body-editor"
              value={draft.body}
              onChange={(e) => change("body", e.target.value)}
              disabled={!editable}
              readOnly={!!busy}
              placeholder="Escreva livremente ou peça à IA uma primeira versão a partir da solicitação. Revise os fatos antes de finalizar."
            />
            <div className="paper-bottom">
              <span>
                {draft.body.trim() ? draft.body.trim().split(/\s+/).length : 0}{" "}
                palavras
              </span>
              <span>
                A numeração e a composição oficial são aplicadas na finalização.
              </span>
            </div>
          </div>
          {o.pdfFileId && (
            <div className="document-footer">
              <Button icon="eye" onClick={() => setPreview(o.pdfFileId!)}>
                Conferir PDF final
              </Button>
              <a
                className="button secondary"
                href={fileUrl(o.pdfFileId)}
                download
              >
                <Icon name="download" size={18} />
                Baixar PDF
              </a>
              {can("create") && (
                <Button
                  disabled={!online || !!busy}
                  busy={busy === "revise"}
                  onClick={() =>
                    run("revise", async () => {
                      const next = await api<Office>(
                        "/offices/" + id + "/revise",
                        "POST",
                      );
                      onOpen(next.id);
                    })
                  }
                >
                  Criar revisão com novo número
                </Button>
              )}
            </div>
          )}
        </section>
        <aside className="assistant-panel">
          <div
            className="tabs side-tabs"
            role="tablist"
            onKeyDown={tabKeys}
            aria-label="Painéis do documento"
          >
            {[
              ["ai", "Redação com IA"],
              ["attachments", `Anexos (${o.attachmentIds.length})`],
              ["history", "Andamento"],
            ].map(([key, label]) => (
              <button
                role="tab"
                key={key}
                aria-selected={panel === key}
                onClick={() => setPanel(key)}
              >
                {label}
              </button>
            ))}
          </div>
          {panel === "ai" && (
            <div className="assistant-content">
              <div className="assistant-intro">
                <span className="assistant-icon">
                  <Icon name="spark" size={23} />
                </span>
                <h2>Uma boa redação começa com os fatos.</h2>
                <p>
                  A IA usa as orientações do gabinete e os dados deste caso.
                  Você confere cada proposta antes de aplicar.
                </p>
              </div>
              {!can("generate") ? (
                <Banner kind="warn">
                  Seu acesso não inclui geração com IA. Você pode consultar e
                  editar conforme suas permissões.
                </Banner>
              ) : !config?.ai.enabled ? (
                <Banner kind="warn">
                  A redação com IA está desativada na configuração publicada. O
                  editor continua disponível.
                </Banner>
              ) : null}
              {currentSuggestion.length === 0 && o.suggestions.length === 0 && (
                <div className="prompt-examples">
                  <span>Você pode pedir</span>
                  {[
                    "Prepare uma primeira versão com base na solicitação.",
                    "Deixe o texto mais objetivo.",
                    "Revise a clareza sem acrescentar fatos.",
                  ].map((text) => (
                    <button
                      key={text}
                      disabled={!editable || !!busy || !can("generate")}
                      onClick={() => setInstruction(text)}
                    >
                      {text}
                      <Icon name="arrow" size={15} />
                    </button>
                  ))}
                </div>
              )}
              {o.suggestions.length > 0 && (
                <div className="suggestions">
                  {[...o.suggestions].reverse().map((s) => (
                    <article className={"suggestion " + s.state} key={s.id}>
                      <div className="suggestion-label">
                        <Icon name="spark" size={16} />
                        <strong>
                          {s.state === "pending"
                            ? "Sugestão para conferir"
                            : s.state === "accepted"
                              ? "Sugestão aceita"
                              : "Sugestão recusada"}
                        </strong>
                        <small>{date(s.createdAt)}</small>
                      </div>
                      <p className="user-prompt">{s.instruction}</p>
                      {s.paragraph && (
                        <span className="badge info">
                          Parágrafo {s.paragraph}
                        </span>
                      )}
                      {s.missing?.length ? (
                        <Banner kind="warn">
                          Confira as informações pendentes:{" "}
                          {s.missing.join("; ")}
                        </Banner>
                      ) : null}
                      <details open={s.state === "pending"}>
                        <summary>Comparar original e proposta</summary>
                        <div className="diff-block original">
                          <span>Original</span>
                          <p>{s.original || "Sem texto anterior."}</p>
                        </div>
                        <div className="diff-block proposed">
                          <span>Proposta da IA · ainda não aplicada</span>
                          <p>{s.proposed}</p>
                        </div>
                      </details>
                      {user.admin && s.references.length > 0 && (
                        <small className="reference-ids">
                          Referências selecionadas: {s.references.join(", ")}
                        </small>
                      )}
                      {s.state === "pending" && (
                        <div className="suggestion-actions">
                          <Button
                            disabled={!noUnsaved || !editable}
                            busy={busy === "accept-" + s.id}
                            kind="primary"
                            icon="check"
                            onClick={() =>
                              run("accept-" + s.id, async () => {
                                replace(
                                  await api(
                                    "/offices/" + id + "/suggestions/" + s.id,
                                    "POST",
                                    { accept: true },
                                  ),
                                );
                                notify(
                                  "Sugestão aceita e confirmada no servidor.",
                                );
                              })
                            }
                          >
                            Aceitar
                          </Button>
                          <Button
                            disabled={!noUnsaved || !editable}
                            onClick={() =>
                              run("reject-" + s.id, async () =>
                                replace(
                                  await api(
                                    "/offices/" + id + "/suggestions/" + s.id,
                                    "POST",
                                    { accept: false },
                                  ),
                                ),
                              )
                            }
                          >
                            Recusar
                          </Button>
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              )}
              <form
                className="chat-composer"
                onSubmit={(e) => {
                  e.preventDefault();
                  run("suggest", async () => {
                    await api("/offices/" + id + "/suggest", "POST", {
                      instruction,
                      version: baseVersion,
                      ...(scope === "paragraph" ? { paragraph } : {}),
                    });
                    replace(await api("/offices/" + id));
                    setInstruction("");
                    notify(
                      "Proposta recebida. Confira a comparação antes de aceitar.",
                    );
                  });
                }}
              >
                <Field label="Pedir uma alteração">
                  <textarea
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    disabled={!editable || !!busy || !can("generate")}
                    rows={3}
                    placeholder="Explique o que deseja mudar…"
                    required
                  />
                </Field>
                <div className="scope-row">
                  <Field label="Alcance">
                    <select
                      value={scope}
                      onChange={(e) => setScope(e.target.value)}
                      disabled={!editable || !!busy}
                    >
                      <option value="all">Texto completo</option>
                      <option value="paragraph">Apenas um parágrafo</option>
                    </select>
                  </Field>
                  {scope === "paragraph" && (
                    <Field label="Parágrafo">
                      <input
                        type="number"
                        min={1}
                        max={Math.max(
                          1,
                          draft.body.split(/\n\s*\n/).filter(Boolean).length,
                        )}
                        value={paragraph}
                        disabled={!!busy}
                        onChange={(e) => setParagraph(Number(e.target.value))}
                      />
                    </Field>
                  )}
                </div>
                {dirty && (
                  <small className="attention">
                    Salve suas alterações antes de solicitar ou aplicar uma
                    proposta.
                  </small>
                )}
                <Button
                  kind="primary"
                  icon="spark"
                  type="submit"
                  busy={busy === "suggest"}
                  disabled={
                    !noUnsaved ||
                    !editable ||
                    !can("generate") ||
                    !config?.ai.enabled ||
                    !instruction.trim()
                  }
                >
                  Gerar proposta
                </Button>
                <small>
                  A geração usa a API configurada e respeita os limites do
                  gabinete.
                </small>
              </form>
            </div>
          )}
          {panel === "attachments" && (
            <div className="attachment-panel">
              <h2>Documentos do caso</h2>
              <p>
                Confira os arquivos e a extração antes de usá-los na redação.
              </p>
              <div className="pdf-composition-note">
                <Icon name="document" size={18} />
                <p>
                  {o.status === "draft"
                    ? config?.layout.appendImages !== false
                      ? "As imagens serão acrescentadas ao final do mesmo PDF do ofício, uma por página, na ordem abaixo. Os demais arquivos seguem como complementares."
                      : "A configuração publicada mantém os arquivos como complementares, separados do PDF do ofício."
                    : imageIds.length
                      ? `${imageIds.length} ${imageIds.length === 1 ? "imagem integra" : "imagens integram"} o PDF final, na ordem preservada na finalização.`
                      : "Os anexos deste ofício são arquivos complementares, separados do PDF final."}
                </p>
              </div>
              {editable && (
                <label
                  className={
                    "upload-zone " + (!online || busy ? "disabled" : "")
                  }
                >
                  <Icon name="upload" size={26} />
                  <strong>Adicionar documento</strong>
                  <span>
                    PDF, Word, imagem ou texto · limite configurado:{" "}
                    {config?.limits.maxUploadMb ?? "—"} MB
                  </span>
                  <input
                    aria-label="Adicionar documento ao ofício"
                    type="file"
                    disabled={!online || !!busy || dirty}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file)
                        run("upload", async () => {
                          const data = new FormData();
                          data.set("file", file);
                          await api(
                            "/offices/" + id + "/attachments",
                            "POST",
                            data,
                          );
                          await reload();
                          notify(
                            "Arquivo recebido pelo servidor. Confira o estado da extração.",
                          );
                        });
                      e.target.value = "";
                    }}
                  />
                </label>
              )}
              {editable && (
                <Button
                  icon="link"
                  disabled={!online || !!busy || dirty}
                  onClick={() => setModal("drive-import")}
                >
                  Adicionar arquivo do Drive
                </Button>
              )}
              {dirty && (
                <p className="attention">
                  Salve o texto antes de alterar os anexos.
                </p>
              )}
              {busy === "upload" && (
                <Banner>
                  Recebendo e preparando o arquivo. Aguarde a confirmação.
                </Banner>
              )}
              {files.length === 0 ? (
                <div className="small-empty">Nenhum anexo neste ofício.</div>
              ) : (
                files.map((file) => (
                  <article className="file-item" key={file.id}>
                    <div className="file-title">
                      <Icon name="document" />
                      <strong>{file.name}</strong>
                    </div>
                    {file.mime.startsWith("image/") && (
                      <button
                        className="attachment-image-button"
                        aria-label={`Consultar imagem ${file.name}`}
                        onClick={() => setPreview(file.id)}
                      >
                        <img
                          className="attachment-thumbnail"
                          alt={`Imagem anexada: ${file.name}`}
                          loading="lazy"
                          src={fileUrl(file.id)}
                        />
                      </button>
                    )}
                    <span
                      className={
                        "badge " +
                        (embeddedIds.has(file.id) ? "info" : "neutral")
                      }
                    >
                      <Icon name="document" size={14} />
                      {embeddedIds.has(file.id)
                        ? `Imagem ${imageIds.indexOf(file.id) + 1} no PDF`
                        : "Arquivo complementar"}
                    </span>
                    {editable && embeddedIds.has(file.id) && (
                      <div
                        className="image-order-actions"
                        role="group"
                        aria-label={`Ordem da imagem ${file.name}`}
                      >
                        <Button
                          kind="ghost"
                          disabled={
                            !noUnsaved ||
                            !attachmentsLoaded ||
                            imageIds.indexOf(file.id) === 0
                          }
                          aria-label={`Mover imagem ${file.name} para cima`}
                          onClick={() => moveImage(file.id, -1)}
                        >
                          ↑ Subir
                        </Button>
                        <Button
                          kind="ghost"
                          disabled={
                            !noUnsaved ||
                            !attachmentsLoaded ||
                            imageIds.indexOf(file.id) === imageIds.length - 1
                          }
                          aria-label={`Mover imagem ${file.name} para baixo`}
                          onClick={() => moveImage(file.id, 1)}
                        >
                          ↓ Descer
                        </Button>
                      </div>
                    )}
                    <small>
                      {bytes(file.size)} ·{" "}
                      {file.driveId
                        ? "Armazenado no Drive"
                        : "Armazenamento local · Drive pendente"}
                    </small>
                    <span
                      className={
                        "badge " +
                        (file.extractionState === "ready" ? "success" : "warn")
                      }
                    >
                      {file.extractionState === "ready"
                        ? "Texto extraído"
                        : file.extractionState === "needs_review"
                          ? "Conferência necessária"
                          : file.extractionState === "unsupported"
                            ? "Extração indisponível"
                            : "Falha na extração"}
                    </span>
                    {file.extractionMessage && <p>{file.extractionMessage}</p>}
                    <div className="file-actions">
                      <Button
                        kind="ghost"
                        icon="eye"
                        onClick={() => setPreview(file.id)}
                      >
                        Consultar
                      </Button>
                      {editable && (
                        <Button
                          kind="ghost"
                          disabled={!online || !!busy || dirty}
                          onClick={() =>
                            run("remove", async () => {
                              await api(
                                "/offices/" + id + "/attachments/" + file.id,
                                "DELETE",
                              );
                              await reload();
                              notify("Anexo retirado deste rascunho.");
                            })
                          }
                        >
                          Retirar
                        </Button>
                      )}
                    </div>
                    {file.extractedText && (
                      <details>
                        <summary>Conferir texto extraído</summary>
                        <pre className="extracted">{file.extractedText}</pre>
                      </details>
                    )}
                  </article>
                ))
              )}
            </div>
          )}
          {panel === "history" && (
            <div className="history-panel">
              <h2>Andamento do ofício</h2>
              {o.history.length ? (
                <ol className="timeline">
                  {[...o.history].reverse().map((h, i) => (
                    <li key={i}>
                      <strong>{h.text}</strong>
                      <time>{date(h.at)}</time>
                    </li>
                  ))}
                </ol>
              ) : (
                <p>Nenhuma movimentação registrada.</p>
              )}
              {o.attempt && (
                <details>
                  <summary>Manifesto do envio confirmado</summary>
                  <p>Assunto: {o.attempt.subject}</p>
                  <p>Versão {o.attempt.officeVersion}</p>
                  <ul className="manifest">
                    {o.attempt.files.map((f) => (
                      <li key={f.id}>
                        <strong>{f.name}</strong>
                        <small>{f.state}</small>
                        <code>{f.sha256}</code>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {o.status === "protocolled" && (
                <section className="response-section">
                  <h3>
                    {o.followup === "closed_with_response"
                      ? "Finalizado com resposta"
                      : "Acompanhamento de respostas"}
                  </h3>
                  {o.responses.length ? (
                    o.responses.map((r) => (
                      <article key={r.id}>
                        <time>{date(r.at)}</time>
                        <p>{r.text}</p>
                        {r.fileIds.map((id) => (
                          <Button
                            key={id}
                            kind="ghost"
                            icon="eye"
                            onClick={() => setPreview(id)}
                          >
                            Consultar documento
                          </Button>
                        ))}
                      </article>
                    ))
                  ) : (
                    <p>
                      Aguardando resposta. Uma resposta recebida não encerra o
                      acompanhamento automaticamente.
                    </p>
                  )}
                  {o.responses.length > 0 &&
                    o.followup !== "closed_with_response" &&
                    can("edit") && (
                      <Button
                        disabled={!online || !!busy}
                        onClick={() => setModal("close-response")}
                      >
                        Conferi a resposta · concluir acompanhamento
                      </Button>
                    )}
                </section>
              )}
            </div>
          )}
        </aside>
      </div>
      {preview && (
        <FilePreview
          id={preview}
          onClose={() => setPreview(null)}
          failed={failed}
        />
      )}
      {draftPreview && (
        <Modal
          title="Prévia do PDF · sem número oficial"
          onClose={() => setDraftPreview(null)}
          wide
        >
          <Banner>
            Confira o texto, as imagens e a ordem das páginas. Esta prévia não
            reserva número, não grava na planilha e não inicia protocolo.
          </Banner>
          <PDFPreview
            sourceUrl={draftPreview}
            name="Prévia do ofício e imagens"
          />
        </Modal>
      )}
      {modal === "drive-import" && (
        <Modal
          title="Adicionar documento do Drive"
          onClose={() => setModal("")}
        >
          <p className="modal-intro">
            Informe o identificador ou o link do arquivo autorizado. A conexão
            do servidor precisa ter acesso ao documento.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              run("drive-import", async () => {
                const match = driveId.match(/\/d\/([\w-]+)/);
                const selectedId = match?.[1] || driveId.trim();
                await api("/offices/" + id + "/attachments/drive", "POST", {
                  driveId: selectedId,
                });
                await reload();
                setModal("");
                setDriveId("");
                notify("Documento do Drive importado. Confira a extração.");
              });
            }}
          >
            <Field label="Link ou identificador do arquivo">
              <input
                value={driveId}
                disabled={!!busy}
                onChange={(e) => setDriveId(e.target.value)}
                required
                autoFocus
              />
            </Field>
            <div className="modal-actions">
              <Button type="button" onClick={() => setModal("")}>
                Cancelar
              </Button>
              <Button
                kind="primary"
                type="submit"
                busy={busy === "drive-import"}
                disabled={!online || dirty}
              >
                Importar documento
              </Button>
            </div>
          </form>
        </Modal>
      )}
      {modal === "share" && (
        <Modal title="Compartilhar ofício" onClose={() => setModal("")}>
          <p className="modal-intro">
            O acesso ao documento respeita as permissões que cada pessoa já
            possui. Compartilhar não libera finalização, IA ou protocolo.
          </p>
          <div className="people-list">
            {people
              .filter(
                (p) =>
                  p.id !== o.createdBy &&
                  p.active &&
                  (p.admin || p.permissions.includes("access")),
              )
              .map((p) => (
                <label key={p.id}>
                  <input
                    type="checkbox"
                    checked={sharing.includes(p.id)}
                    disabled={!!busy}
                    onChange={(e) =>
                      setSharing((current) =>
                        e.target.checked
                          ? [...current, p.id]
                          : current.filter((id) => id !== p.id),
                      )
                    }
                  />
                  <span>
                    <strong>{p.name}</strong>
                    <small>
                      {p.admin
                        ? "Administrador"
                        : p.permissions
                            .map((p) => permissionLabels[p])
                            .join(" · ")}
                    </small>
                  </span>
                </label>
              ))}
          </div>
          <div className="modal-actions">
            <Button onClick={() => setModal("")}>Cancelar</Button>
            <Button
              kind="primary"
              busy={busy === "sharing"}
              onClick={() =>
                run("sharing", async () => {
                  replace(
                    await api("/offices/" + id + "/share", "POST", {
                      userIds: sharing,
                    }),
                  );
                  setModal("");
                  notify("Compartilhamento atualizado no servidor.");
                })
              }
            >
              Salvar compartilhamento
            </Button>
          </div>
        </Modal>
      )}
      {modal === "finalize" && (
        <Modal
          title="Conferir e finalizar ofício"
          onClose={() => setModal("")}
          wide
        >
          <div className="confirmation-summary">
            <div>
              <span>Destinatário</span>
              <strong>
                {config?.recipients.find((r) => r.id === draft.recipientId)
                  ?.name || "Não definido"}
              </strong>
            </div>
            <div>
              <span>Assunto</span>
              <strong>{draft.subject || "Não informado"}</strong>
            </div>
          </div>
          <div className="text-preview">{draft.body}</div>
          <Button
            icon="eye"
            busy={busy === "preview"}
            disabled={!noUnsaved || !attachmentsLoaded}
            onClick={showDraftPreview}
          >
            Conferir prévia do PDF
          </Button>
          <h3>Imagens no mesmo PDF ({imageFiles.length})</h3>
          {imageFiles.length > 0 ? (
            <ol className="composition-list">
              {imageFiles.map((file) => (
                <li key={file.id}>
                  <button
                    className="text-button"
                    onClick={() => setPreview(file.id)}
                  >
                    {file.name}
                  </button>
                </li>
              ))}
            </ol>
          ) : (
            <p className="muted">Nenhuma imagem será acrescentada ao PDF.</p>
          )}
          <h3>Arquivos complementares ({complementaryFiles.length})</h3>
          <ul className="plain-list">
            {complementaryFiles.map((f) => (
              <li key={f.id}>
                <button
                  className="text-button"
                  onClick={() => setPreview(f.id)}
                >
                  {f.name}
                </button>
              </li>
            ))}
          </ul>
          <Banner>
            Ao finalizar, o sistema valida os dados, reserva a numeração anual e
            prepara o PDF e os registros externos. A versão final será
            preservada; uma revisão posterior terá um novo número.
          </Banner>
          <div className="modal-actions">
            <Button onClick={() => setModal("")}>Continuar editando</Button>
            <Button
              kind="primary"
              busy={busy === "finalize"}
              disabled={!noUnsaved || !attachmentsLoaded}
              onClick={() =>
                run("finalize", async () => {
                  await api("/offices/" + id + "/finalize", "POST", {
                    version: baseVersion,
                  });
                  setModal("");
                  await reload();
                  notify(
                    "Pedido de finalização recebido. Acompanhe o processamento.",
                  );
                })
              }
            >
              Finalizar ofício
            </Button>
          </div>
        </Modal>
      )}
      {modal === "protocol" && (
        <Modal
          title="Confirmar envio ao Solar"
          onClose={() => setModal("")}
          wide
        >
          <p className="modal-intro">
            Confira exatamente o que será enviado. Após esta confirmação, o
            sistema realizará o preenchimento e os uploads conforme a integração
            habilitada.
          </p>
          <div className="confirmation-summary">
            <div>
              <span>Documento</span>
              <strong>
                Ofício {o.number}/{o.year}
              </strong>
              <button
                className="text-button"
                onClick={() => setPreview(o.pdfFileId!)}
              >
                Conferir PDF
              </button>
            </div>
            <div>
              <span>Destinatário</span>
              <strong>
                {o.recipientName ||
                  config?.recipients.find((r) => r.id === o.recipientId)
                    ?.name ||
                  "Não definido"}
              </strong>
            </div>
            <div>
              <span>Assunto aprovado</span>
              <strong>{o.subject}</strong>
            </div>
          </div>
          <h3>Arquivos incluídos no envio</h3>
          <ul className="plain-list">
            <li>
              <Icon name="document" size={18} />
              Documento principal · Ofício {o.number}/{o.year}
              {imageIds.length > 0 && (
                <small>
                  Inclui {imageIds.length}{" "}
                  {imageIds.length === 1 ? "imagem" : "imagens"}
                </small>
              )}
            </li>
            {complementaryFiles.map((f) => (
              <li key={f.id}>
                <Icon name="document" size={18} />
                {f.name} <small>{bytes(f.size)}</small>
              </li>
            ))}
          </ul>
          {imageFiles.length > 0 && (
            <details className="composition-details">
              <summary>Imagens já incluídas no documento principal</summary>
              <ol className="composition-list">
                {imageFiles.map((file) => (
                  <li key={file.id}>{file.name}</li>
                ))}
              </ol>
              <p>
                Estas imagens não serão enviadas novamente como arquivos
                separados.
              </p>
            </details>
          )}
          <Banner kind="warn">
            A confirmação inicia uma tentativa persistente. Se uma assinatura
            humana for necessária, o andamento indicará a intervenção
            disponível. Não confirme novamente em caso de resultado incerto.
          </Banner>
          <div className="modal-actions">
            <Button onClick={() => setModal("")}>Adiar</Button>
            <Button
              kind="primary"
              icon="send"
              busy={busy === "protocol"}
              disabled={!noUnsaved || !attachmentsLoaded}
              onClick={() =>
                run("protocol", async () => {
                  await api("/offices/" + id + "/protocol", "POST", {
                    version: baseVersion,
                  });
                  setModal("");
                  await reload();
                  notify(
                    "Confirmação recebida pelo servidor. Acompanhe o envio.",
                  );
                })
              }
            >
              Confirmar envio
            </Button>
          </div>
        </Modal>
      )}
      {modal === "close-response" && (
        <Modal title="Concluir acompanhamento" onClose={() => setModal("")}>
          <p>
            Você conferiu as respostas e deseja marcar este ofício como
            finalizado com resposta?
          </p>
          <p className="muted">Esta conclusão não cria uma nova recompensa.</p>
          <div className="modal-actions">
            <Button onClick={() => setModal("")}>Voltar</Button>
            <Button
              kind="primary"
              onClick={() =>
                run("close-response", async () => {
                  replace(
                    await api("/offices/" + id + "/close-response", "POST"),
                  );
                  setModal("");
                  notify("Acompanhamento concluído no servidor.");
                })
              }
            >
              Confirmar conclusão
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}
export function FilePreview({
  id,
  onClose,
  failed,
}: {
  id: string;
  onClose: () => void;
  failed: (e: any) => void;
}) {
  const [file, setFile] = useState<FileRecord | null>(null),
    [error, setError] = useState("");
  useEffect(() => {
    api<FileRecord>("/file-metadata/" + id)
      .then(setFile)
      .catch((e) => {
        setError(e.message);
        failed(e);
      });
  }, [id]);
  return (
    <Modal title={file?.name || "Consultar documento"} onClose={onClose} wide>
      <div className="preview-toolbar">
        <span>
          {file
            ? `${bytes(file.size)} · ${file.mime}`
            : "Consultando acesso ao arquivo…"}
        </span>
        {file && (
          <a className="button secondary" href={fileUrl(file.id)} download>
            <Icon name="download" size={17} />
            Baixar arquivo
          </a>
        )}
      </div>
      {error ? (
        <Banner kind="danger">{error}</Banner>
      ) : !file ? (
        <div className="loading">
          <span className="spinner" />
          Carregando documento…
        </div>
      ) : file.mime === "application/pdf" ? (
        <PDFPreview id={file.id} name={file.name} />
      ) : file.mime.startsWith("image/") ? (
        <div className="image-viewer">
          <img alt={"Documento: " + file.name} src={fileUrl(file.id)} />
        </div>
      ) : (
        <div className="unsupported-preview">
          <Icon name="document" size={30} />
          <h3>Consulte o conteúdo extraído ou baixe o arquivo.</h3>
          <p>Este formato não possui visualização direta no navegador.</p>
          {file.extractedText && (
            <pre className="extracted">{file.extractedText}</pre>
          )}
        </div>
      )}
    </Modal>
  );
}
